import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AdBanner Component - Displays ads from admin-managed database
 * 
 * Placement options:
 * - sidebar: Shows in sidebar area
 * - inline: Shows between content
 * - banner: Shows at bottom of screen
 */

// Fallback sample ads - only used if explicitly enabled or no ads exist
// Set to empty array to disable fallback ads
const FALLBACK_ADS = [];

export function AdBanner({ placement = 'banner', onDismiss, className = '' }) {
    const [ad, setAd] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAd = async () => {
            try {
                const res = await fetch(`${API}/api/ads/active?placement=${placement}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.ads && data.ads.length > 0) {
                        // Pick a random ad from available ones
                        const randomAd = data.ads[Math.floor(Math.random() * data.ads.length)];
                        setAd(randomAd);
                        
                        // Track impression
                        fetch(`${API}/api/ads/${randomAd.id}/impression`, { method: 'POST' }).catch(() => {});
                    } else {
                        // No active ads - don't show anything
                        setAd(null);
                    }
                } else {
                    // API error - don't show fallback ads
                    setAd(null);
                }
            } catch {
                // Network error - don't show fallback ads
                setAd(null);
            } finally {
                setLoading(false);
            }
        };
        
        fetchAd();
    }, [placement]);

    const handleDismiss = () => {
        setDismissed(true);
        onDismiss?.();
    };

    const handleClick = () => {
        if (ad?.id && !ad.id.startsWith('fallback')) {
            fetch(`${API}/api/ads/${ad.id}/click`, { method: 'POST' }).catch(() => {});
        }
        if (ad?.cta_url) {
            window.open(ad.cta_url, '_blank', 'noopener,noreferrer');
        }
    };

    if (dismissed || loading || !ad) return null;

    const bgGradient = ad.bg_gradient || 'from-emerald-500/10 to-emerald-600/5';
    const borderColor = ad.border_color || 'border-emerald-500/20';

    // Different layouts based on placement
    if (placement === 'sidebar') {
        return (
            <div 
                className={`p-3 rounded-lg bg-gradient-to-br ${bgGradient} border ${borderColor} ${className}`}
                data-testid="ad-sidebar"
            >
                <div className="flex items-start justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sponsored</span>
                    <button 
                        onClick={handleDismiss}
                        className="p-0.5 hover:bg-background/50 rounded transition-colors"
                        aria-label="Dismiss ad"
                    >
                        <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                </div>
                <h4 className="font-medium text-sm mb-1">{ad.title}</h4>
                <p className="text-xs text-muted-foreground mb-2">{ad.description}</p>
                <button 
                    onClick={handleClick}
                    className="text-xs font-medium text-primary hover:underline"
                >
                    {ad.cta_text || 'Learn More'} →
                </button>
            </div>
        );
    }

    if (placement === 'inline') {
        return (
            <div 
                className={`max-w-3xl mx-auto my-4 p-4 rounded-xl bg-gradient-to-r ${bgGradient} border ${borderColor} ${className}`}
                data-testid="ad-inline"
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground px-1.5 py-0.5 bg-background/50 rounded">Ad</span>
                            <h4 className="font-medium text-sm">{ad.title}</h4>
                        </div>
                        <p className="text-xs text-muted-foreground">{ad.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={handleClick}
                            className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                        >
                            {ad.cta_text || 'Learn More'}
                        </button>
                        <button 
                            onClick={handleDismiss}
                            className="p-1 hover:bg-background/50 rounded transition-colors"
                            aria-label="Dismiss ad"
                        >
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Default: bottom banner
    return (
        <div 
            className={`fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border shadow-lg ${className}`}
            data-testid="ad-banner"
        >
            <div className="max-w-7xl mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground px-2 py-1 bg-muted rounded">Sponsored</span>
                        <div>
                            <h4 className="font-medium text-sm">{ad.title}</h4>
                            <p className="text-xs text-muted-foreground">{ad.description}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={handleClick}
                            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            {ad.cta_text || 'Learn More'}
                        </button>
                        <button 
                            onClick={handleDismiss}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                            aria-label="Dismiss ad"
                        >
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Hook to determine if ads should be shown
 * Returns true for free tier users
 */
export function useShowAds(isPremium) {
    return !isPremium;
}

export default AdBanner;
